import {
  FileUploader,
  Flex,
  Form,
  HintIcon,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import { RelatedMaterialFormSchema } from '@/features/project/projects/_models/related-material';
import { useForm } from '@/hooks';
import { zodResolver } from '@hookform/resolvers/zod';
import { type FC, useState } from 'react';
import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof RelatedMaterialFormSchema>;

type Props = {
  initialValues: Partial<FormValues>;
  onSubmit: ({
    values,
    file,
  }: {
    values: z.infer<typeof RelatedMaterialFormSchema>;
    file: File | null;
  }) => void;
};

export const RelatedMaterialForm: FC<Props> = ({ initialValues, onSubmit }) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isDirty, errors, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(RelatedMaterialFormSchema),
  });

  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const { fields: attachmentFields, remove: removeAttachment } =
    useFieldArray<FormValues>({
      control,
      name: 'attachments',
    });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(async (values: FormValues) => {
        await onSubmit({
          values,
          file: uploadedFile,
        });
      })}
    >
      <Form.Item
        label={
          <Flex gap={6}>
            {t('related-material')}
            <HintIcon hint={t('hint-upload-image-only')} />
          </Flex>
        }
      >
        <FileUploader
          files={uploadedFile ? [uploadedFile] : []}
          onChange={(acceptedFiles) => {
            setUploadedFile(acceptedFiles[0] ?? null);
          }}
          handleCancel={() => {
            setUploadedFile(null);
          }}
          storedFiles={{
            items: attachmentFields.map((field) => ({
              name: field.fileName,
              url: field.url,
              fileContentType: field.fileContentType,
            })),
            onCancel: (index) => {
              removeAttachment(index);
            },
          }}
          acceptableImage
          maxFiles={1}
          disabled={!!uploadedFile || attachmentFields.length > 0}
        />
      </Form.Item>
      <TextAreaInputField
        name="additionalExplanation"
        label={t('remark')}
        hint={t('hint-related-material-remark')}
        control={control}
        errors={errors}
        rows={10}
      />
      <SubmitButton
        text={t('save')}
        loading={isSubmitting}
        disabled={!isDirty && !uploadedFile}
      />
    </Form>
  );
};
